import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, intlShape } from '../../util/reactIntl';
import classNames from 'classnames';
import { ACCOUNT_SETTINGS_PAGES } from '../../routeConfiguration';
import { propTypes } from '../../util/types';
import {
  Avatar,
  InlineTextButton,
  Logo,
  Menu,
  MenuLabel,
  MenuContent,
  MenuItem,
  NamedLink,
} from '../../components';
import { TopbarSearchForm } from '../../forms';

import css from './TopbarDesktop.css';

const TopbarDesktop = props => {
  const {
    className,
    currentUser,
    currentPage,
    rootClassName,
    currentUserHasListings,
    notificationCount,
    intl,
    isAuthenticated,
    onLogout,
    onSearchSubmit,
    initialSearchFormValues,
  } = props;
  const [mounted, setMounted] = useState(false);

  const [navbarScroll, setNavbarScrolled] = useState(false);

  const changeBackground = () => {
    if (window.scrollY >= 72) {
      setNavbarScrolled(true)
    } else {
      setNavbarScrolled(false)
    }
  }

  // Add event listener when component is mounted

  if (typeof window !== "undefined") {
    // browser code
    window.addEventListener("scroll", changeBackground)
  }

  useEffect(() => {
    setMounted(true);
  }, []);

  const authenticatedOnClientSide = mounted && isAuthenticated;
  const isAuthenticatedOrJustHydrated = isAuthenticated || !mounted;

const classes = navbarScroll ? classNames(rootClassName || css.navbarScroll, className) : classNames(rootClassName || css.root, className);

  const search = (
    <TopbarSearchForm
      className={css.searchLink}
      desktopInputRoot={css.topbarSearchWithLeftPadding}
      onSubmit={onSearchSubmit}
      initialValues={initialSearchFormValues}
      isHIW={false}
    />
  );

  const notificationDot = notificationCount > 0 ? <div className={css.notificationDot} /> : null;

  const inboxLink = authenticatedOnClientSide ? (
    <NamedLink
      className={css.inboxLink}
      name="InboxPage"
      params={{ tab: currentUserHasListings ? 'sales' : 'orders' }}
    >
      <span className={css.inbox}>
        <FormattedMessage id="TopbarDesktop.inbox" />
        {notificationDot}
      </span>
    </NamedLink>
  ) : null;

  const currentPageClass = page => {
    const isAccountSettingsPage =
      page === 'AccountSettingsPage' && ACCOUNT_SETTINGS_PAGES.includes(currentPage);
    return currentPage === page || isAccountSettingsPage ? css.currentPage : null;
  };


  const profileMenu = authenticatedOnClientSide ? (
    <Menu>
      <MenuLabel className={css.profileMenuLabel} isOpenClassName={css.profileMenuIsOpen}>
        <Avatar className={css.avatar} user={currentUser} disableProfileLink />
      </MenuLabel>
      <MenuContent className={css.profileMenuContent}>
        <MenuItem key="GetStarted">
          <NamedLink
            className={classNames(css.yourListingsLink, currentPageClass('GetStarted'))}
            name="GetStarted"
          >
            <span className={css.menuItemBorder} />
            <FormattedMessage id="TopbarDesktop.getStartedLink" />
          </NamedLink>
        </MenuItem>
        <MenuItem key="ManageListingsPage">
          <NamedLink
            className={classNames(css.yourListingsLink, currentPageClass('ManageListingsPage'))}
            name="ManageListingsPage"
          >
            <span className={css.menuItemBorder} />
            <FormattedMessage id="TopbarDesktop.yourListingsLink" />
          </NamedLink>
        </MenuItem>
        <MenuItem key="ProfileSettingsPage">
          <NamedLink
            className={classNames(css.profileSettingsLink, currentPageClass('ProfileSettingsPage'))}
            name="ProfileSettingsPage"
          >
            <span className={css.menuItemBorder} />
            <FormattedMessage id="TopbarDesktop.profileSettingsLink" />
          </NamedLink>
        </MenuItem>
        <MenuItem key="AccountSettingsPage">
          <NamedLink
            className={classNames(css.yourListingsLink, currentPageClass('AccountSettingsPage'))}
            name="AccountSettingsPage"
          >
            <span className={css.menuItemBorder} />
            <FormattedMessage id="TopbarDesktop.accountSettingsLink" />
          </NamedLink>
        </MenuItem>
        <MenuItem key="SavedPage">
          <NamedLink
            className={classNames(css.yourListingsLink, currentPageClass('SavedPage'))}
            name="SavedPage"
          >
            <span className={css.menuItemBorder} />
            <FormattedMessage id="TopbarDesktop.savedListingsLink" />
          </NamedLink>
        </MenuItem>
        <MenuItem key="VerifyCertificatePage">
          <NamedLink
            className={classNames(css.yourListingsLink, currentPageClass('VerifyCertificatePage'))}
            name="VerifyCertificatePage"
          >
            <span className={css.menuItemBorder} />
            <FormattedMessage id="TopbarDesktop.verifyCertificateLink" />
          </NamedLink>
        </MenuItem>
        <MenuItem key="logout">
          <InlineTextButton rootClassName={css.logoutButton} onClick={onLogout}>
            <span className={css.menuItemBorder} />
            <FormattedMessage id="TopbarDesktop.logout" />
          </InlineTextButton>
        </MenuItem>
      </MenuContent>
    </Menu>
  ) : null;

  const signupLink = isAuthenticatedOrJustHydrated ? null : (
    <NamedLink name="SignupPage" className={css.signupLink}>
      <span className={css.signup}>
        <FormattedMessage id="TopbarDesktop.signup" />
      </span>
    </NamedLink>
  );

  const loginLink = isAuthenticatedOrJustHydrated ? null : (
    <NamedLink name="LoginPage" className={css.loginLink}>
      <span className={css.login}>
        <FormattedMessage id="TopbarDesktop.login" />
      </span>
    </NamedLink>
  );

  const moreMenu = (
    <Menu>
      <MenuLabel className={css.moreMenuLabel} isOpenClassName={css.moreMenuIsOpen}>
        <FormattedMessage id="TopbarDesktop.more" />
        <i className="angle down icon"></i>
      </MenuLabel>
      <MenuContent className={css.moreMenuContent}>
      <MenuItem key="HowItWorksPage">
          <NamedLink
            className={classNames(css.howitWorksLink, currentPageClass('HowItWorksPage'))}
            name="HowItWorksPage"
          >
            <span className={css.menuItemBorder} />
            <FormattedMessage id="TopbarDesktop.howItWorksLink" />
          </NamedLink>
        </MenuItem>
        <MenuItem key="AboutPage">
          <NamedLink
            className={classNames(css.aboutPageLink, currentPageClass('AboutPage'))}
            name="AboutPage"
          >
            <span className={css.menuItemBorder} />
            <FormattedMessage id="TopbarDesktop.aboutPageLink" />
          </NamedLink>
        </MenuItem>
        <MenuItem key="HostPage">
          <NamedLink
            className={classNames(css.hostPageLink, currentPageClass('HostPage'))}
            name="HostPage"
          >
            <span className={css.menuItemBorder} />
            <FormattedMessage id="TopbarDesktop.hostPageLink" />
          </NamedLink>
        </MenuItem>
        <MenuItem key="Contact">
          <NamedLink
            className={css.hostPageLink}
            name="AboutPage"
            to={{ hash: '#contact' }}
          >
            <span className={css.menuItemBorder} />
            <FormattedMessage id="TopbarDesktop.contactLink" />
          </NamedLink>
        </MenuItem>
        <MenuItem key="FAQPage">
          <NamedLink
            className={classNames(css.hostPageLink, currentPageClass('FAQPage'))}
            name="FAQPage"
          >
            <span className={css.menuItemBorder} />
            <FormattedMessage id="TopbarDesktop.faqLink" />
          </NamedLink>
        </MenuItem>
      </MenuContent>
    </Menu>
  );

  const examplesMenu = (
    <Menu>
      <MenuLabel className={css.moreMenuLabel} isOpenClassName={css.moreMenuIsOpen}>
        <FormattedMessage id="TopbarDesktop.examples" />
        <i className="angle down icon"></i>
      </MenuLabel>
      <MenuContent className={css.moreMenuContent}>
        <MenuItem key="PersonalTrainingPage">
          <NamedLink
            className={classNames(css.aboutPageLink, currentPageClass('PersonalTrainingPage'))}
            name="PersonalTrainingPage"
          >
            <span className={css.menuItemBorder} />
            <FormattedMessage id="TopbarDesktop.personalTrainingLink" />
          </NamedLink>
        </MenuItem>
        <MenuItem key="YogaPage">
          <NamedLink
            className={classNames(css.hostPageLink, currentPageClass('YogaPage'))}
            name="YogaPage"
          >
            <span className={css.menuItemBorder} />
            <FormattedMessage id="TopbarDesktop.yogaLink" />
          </NamedLink>
        </MenuItem>
        <MenuItem key="GroupFitnessPage">
          <NamedLink
            className={css.hostPageLink}
            name="GroupFitnessPage"
          >
            <span className={css.menuItemBorder} />
            <FormattedMessage id="TopbarDesktop.groupFitnessLink" />
          </NamedLink>
        </MenuItem>
        <MenuItem key="PilatesPage">
          <NamedLink
            className={classNames(css.hostPageLink, currentPageClass('PilatesPage'))}
            name="PilatesPage"
          >
            <span className={css.menuItemBorder} />
            <FormattedMessage id="TopbarDesktop.pilatesLink" />
          </NamedLink>
        </MenuItem>
        <MenuItem key="CyclesPage">
          <NamedLink
            className={classNames(css.hostPageLink, currentPageClass('CyclesPage'))}
            name="CyclesPage"
          >
            <span className={css.menuItemBorder} />
            <FormattedMessage id="TopbarDesktop.cyclesLink" />
          </NamedLink>
        </MenuItem>
      </MenuContent>
    </Menu>
  );

  return (
    <nav className={classes}>
      <NamedLink className={css.logoLink} name="LandingPage">
        <Logo
          format="desktop"
          className={css.logo}
          alt={intl.formatMessage({ id: 'TopbarDesktop.logo' })}
        />
      </NamedLink>
      {search}
      {examplesMenu}
      {moreMenu}
      <NamedLink className={css.createListingLink} name="NewListingPage">
        <span className={css.createListing}>
          <FormattedMessage id="TopbarDesktop.createListing" />
        </span>
      </NamedLink>
      {inboxLink}
      {profileMenu}
      {signupLink}
      {loginLink}
    </nav>
  );
};

const { bool, func, object, number, string } = PropTypes;

TopbarDesktop.defaultProps = {
  rootClassName: null,
  className: null,
  currentUser: null,
  currentPage: null,
  notificationCount: 0,
  initialSearchFormValues: {},
};

TopbarDesktop.propTypes = {
  rootClassName: string,
  className: string,
  currentUserHasListings: bool.isRequired,
  currentUser: propTypes.currentUser,
  currentPage: string,
  isAuthenticated: bool.isRequired,
  onLogout: func.isRequired,
  notificationCount: number,
  onSearchSubmit: func.isRequired,
  initialSearchFormValues: object,
  intl: intlShape.isRequired,
};

export default TopbarDesktop;
